import React, { Component, ErrorInfo, ReactNode } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  public render() {
    if (this.state.hasError) {
      const isDev = process.env.NODE_ENV === 'development';
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Something went wrong. 🚨</h2>
          <p>Please refresh the page or try again later.</p>
          <button onClick={() => window.location.reload()}>Reload</button>
          {isDev && this.state.error && (
            <pre
              style={{
                textAlign: 'left',
                whiteSpace: 'pre-wrap',
                background: '#f5f5f5',
                color: '#b00020',
                padding: '12px',
                marginTop: '16px',
                maxHeight: '400px',
                overflow: 'auto',
              }}
            >
              {this.state.error.toString()}
              {this.state.errorInfo?.componentStack}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary = () => {
  const error = useRouteError(); // Get the error from React Router

  console.error('Caught by ErrorBoundary:', error);

  let errorMessage = 'An unexpected error occurred.';
  let errorStack: string | undefined;

  if (isRouteErrorResponse(error)) {
    // Error comes from a failed route request
    errorMessage = `${error.status} ${error.statusText}`;
  } else if (error instanceof Error) {
    // Error is a JavaScript Error object
    errorMessage = error.message;
    errorStack = error.stack;
  }

  const isDev = process.env.NODE_ENV === 'development';

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2>Oops! Something went wrong. Contact tech support 🤣🤣🤣</h2>
      <p>{errorMessage}</p>
      <a href='/'>Go back home</a>
      {isDev && errorStack && (
        <pre
          style={{
            textAlign: 'left',
            whiteSpace: 'pre-wrap',
            background: '#f5f5f5',
            color: '#b00020',
            padding: '12px',
            marginTop: '16px',
            maxHeight: '400px',
            overflow: 'auto',
          }}
        >
          {errorStack}
        </pre>
      )}
    </div>
  );
};
